// Site-wide Organization JSON-LD — rendered once in <App> so EVERY route
// (not just the landing page) carries the identity/footprint schema for
// Google's knowledge panel and AI answer engines.
import { NODES, COUNTRIES_SERVED, AU_STATES, NAP } from '@/constants/locations';

// @type ProfessionalService (LocalBusiness ⊂ Organization) — a valid, rich-result-eligible
// type; "Consultant" is not a schema.org type and made Google ignore the whole block.
// Enhanced Organization Schema - "THE IDENTITY PATCH"
// Purpose: Stop Google/AI from confusing DD Consulting with hardware distributors,
// AND advertise the full geographic footprint (AU HQ + 7 satellite offices).
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": "https://ddconsult.com.au/#organization",
  "name": "DD Consulting",
  "legalName": "DD Consulting Pty Ltd",
  "alternateName": ["DD Consulting Pty Ltd", "DD Consulting Australia"],
  "url": "https://ddconsult.com.au/",
  "logo": "https://ddconsult.com.au/logo.png",
  "image": "https://customer-assets.emergentagent.com/job_modernbrutalist/artifacts/ofzw1niq_Options%205-transparent%20background%20landscape%20copy.png",
  "description": "Australian-headquartered strategic engineering consultancy. Enterprise Modernisation and Venture Product Engineering delivered across Australia and seven international satellite offices.",
  "slogan": "Sydney HQ · Australia-wide · Global engineering bench",
  "foundingDate": "2020",
  "disambiguatingDescription": "DD Consulting is an independent Australian strategic technology consultancy. We are not affiliated with Dicker Data or other hardware distributors.",
  "foundingLocation": {
    "@type": "Place",
    "name": "Sydney, Australia"
  },
  // Multi-tier areaServed: 8 Australian states/territories + 7 countries.
  // AU stays first so local rank signals are preserved.
  "areaServed": [
    ...AU_STATES.map((s) => ({
      "@type": "AdministrativeArea",
      "name": s.name,
      "containedInPlace": { "@type": "Country", "name": "Australia" },
    })),
    ...COUNTRIES_SERVED.map((c) => ({
      "@type": "Country",
      "name": c.name,
    })),
  ],
  // Full 8-node location roster — HQ first, then satellites in registry order.
  "location": NODES.map((n) => ({
    "@type": n.isHQ ? "Place" : "Place",
    "name": `DD Consulting — ${n.city}`,
    "address": n.isHQ
      ? NAP.postalAddress()
      : {
          "@type": "PostalAddress",
          "addressLocality": n.city,
          "addressCountry": n.countryCode,
        },
    ...(n.latitude && n.longitude
      ? {
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": String(n.latitude),
            "longitude": String(n.longitude),
          },
        }
      : {}),
    ...(n.isHQ
      ? { "additionalType": "https://schema.org/Corporation" }
      : {}),
  })),
  "knowsAbout": [
    "Enterprise Modernisation",
    "Venture Product Engineering",
    "APRA CPS 234 Compliance",
    "Australian Data Sovereignty",
    "Agentic AI Governance",
    "Technical Due Diligence",
    "ISO 42001 AI Management System",
    "ISO 27001 Alignment",
    "NIST AI Risk Management Framework",
  ],
  // Verified Google Business Profile — strongest NAP signal; must match the
  // listing (name "DD Consulting Pty Ltd", Norwest address, +61 484 105 707).
  "hasMap": NAP.googleBusinessProfile,
  "sameAs": [
    NAP.googleBusinessProfile,
    "https://www.linkedin.com/company/dd-consulting-pty-ltd/",
    "https://www.facebook.com/profile.php?id=61584636043506",
    "https://www.instagram.com/dd__consulting"
  ],
  "telephone": NAP.phoneE164,
  "email": NAP.email,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": NAP.phoneE164,
    "email": NAP.email,
    "contactType": "sales",
    "areaServed": "AU",
    "availableLanguage": "English"
  },
  // Primary postal address = registered HQ (NAP source of truth: constants/locations.js).
  "address": NAP.postalAddress(),
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "-33.7318",
    "longitude": "150.9636"
  },
  "taxID": "65 646 596 867",
  "vatID": "ABN 65 646 596 867"
};

// Service Schema - "THE SERVICE DEFINITIONS"
// Purpose: Tell search engines about two distinct products (Enterprise & Venture)
